import { Button, Checkbox, Form, Input, Tabs } from "antd";
import { Alert, Col, Row } from "antd/es";
import moment from "moment";
import React, { FC } from "react";
import Icon from "../../../assets/icons/icon";
import DocumentsUpload from "../../../components/documentsUpload";
import DateSelect from "../../../components/form/dateSelect";
import EnumRadio from "../../../components/form/EnumRadio";
import EnumSelect from "../../../components/form/enumSelect";
import InputNum from "../../../components/form/inputNum";
import InputVin from "../../../components/form/inputVin";
import LoadingContainer from "../../../components/loadingContainer";
import UserStore from "../../../contexts/userStore";
import _t from "../../../lang/translate";
import { taxFileUrl } from "../../../services/taxService";
import { RemoteVehicleData, ScrapedVehicleType } from "../../../types/appTypes";
import { TaxCase } from "../../../types/taxTypes";
import TaxVehicleForm from "./admin/taxVehicleForm";

const { TabPane } = Tabs;
const { TextArea } = Input;

interface TaxFetchCarFCProps {
  uploadRef?: React.RefObject<HTMLDivElement>;
  onTabChange: React.Dispatch<React.SetStateAction<"auto" | "manual">>;
  activeTab: string;
  taxCase?: TaxCase;
  isAdmin: boolean;
  vinVehicle: RemoteVehicleData | null
  linkVehicle: ScrapedVehicleType | null
  firstRegDateDisabled: boolean;
  mileageDisabled: boolean;
  setFirstRegDateDisabled: React.Dispatch<React.SetStateAction<boolean>>
  setMileageDisabled: React.Dispatch<React.SetStateAction<boolean>>
  loadingVin: boolean;
  loadingLink: boolean;

}

const TaxFetchCarFC: FC<TaxFetchCarFCProps> = ({
  uploadRef, taxCase, onTabChange, isAdmin, activeTab, vinVehicle, linkVehicle,
  firstRegDateDisabled, mileageDisabled, setFirstRegDateDisabled, setMileageDisabled,
  loadingLink, loadingVin
}) => {
  const { hasPermission } = UserStore.useContainer();

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const evalTypeElement = (
    <Form.Item
      name={["evaluationTypeId"]}
      label={_t("evaluating_type")}
      rules={[{ required: activeTab === "auto" }]}
    >
      <EnumRadio nameKey={["taxCase", "evaluationType"]} />
    </Form.Item>
  );

  return (
    <div>
      <Tabs activeKey={activeTab} destroyInactiveTabPane onChange={(key) => onTabChange(key as any)}>
        <TabPane tab={_t("auto")} key="auto">
          <LoadingContainer className="mb-2" showLogo loading={loadingLink}>
            <Form.Item
              wrapperCol={{ span: 24 }}
              className="mb-05"
              name={["vehicle", "link"]}
            >
              <Input size="large" placeholder={_t("insert_link")} prefix={<Icon name="link-outline" />} />
            </Form.Item>
            <LinkPreview vehicle={linkVehicle} />
          </LoadingContainer>

          <LoadingContainer className="mb-2" showLogo loading={loadingVin}>
            <Form.Item
              wrapperCol={{ span: 24 }}
              name={["vehicle", "vin"]}
              className="mb-05"
            >
              <InputVin size="large" placeholder={_t("insert_vin_here")} prefix={<Icon name="barcode-outline" />} />
            </Form.Item>
            <VinPreview vehicle={vinVehicle} />
          </LoadingContainer>

          <Form.Item label=" " name={["flags", "isPriority"]} valuePropName="checked">
            <Checkbox>{_t("high_priority_with_example")}</Checkbox>
          </Form.Item>
          {evalTypeElement}
          <Form.Item label={_t("firstRegDate")} shouldUpdate>
            <Row gutter={[8, 8]}>
              <Col flex={1}>
                <Form.Item noStyle
                  name={["vehicle", "firstRegDate"]}
                >
                  <DateSelect disabled={firstRegDateDisabled} disabledDate={
                    (currentDate) => currentDate && currentDate > moment().endOf('day')
                  }
                  />
                </Form.Item>
              </Col>
              <Col flex={"0 0 auto"}>
                <Button type="text"
                  onClick={() => setFirstRegDateDisabled(prev => !prev)}
                  icon={<Icon name="brush-outline" />} />
              </Col>
            </Row>
          </Form.Item>

          <Form.Item label={_t("mileage")} shouldUpdate>
            <Row gutter={[8, 8]}>
              <Col flex={1}>
                <Form.Item name={["vehicle", "mileage"]}>
                  <InputNum disabled={mileageDisabled} step={1000} min={0} suffix={_t("mileage_unit")} />
                </Form.Item>
              </Col>
              <Col flex={"0 0 auto"}>
                <Button type="text"
                  onClick={() => setMileageDisabled(prev => !prev)}
                  icon={<Icon name="brush-outline" />} />
              </Col>
            </Row>
          </Form.Item>
          <Form.Item
            className="mb-05"
            name={["vehicle", "useCaseId"]}
            label={_t("special_use")}
          >
            <EnumSelect nameKey={["vehicle", "useCase"]} defaultActiveFirstOption />
          </Form.Item>
        </TabPane>
        <TabPane tab={_t("manual")} key="manual">
          {evalTypeElement}
          <TaxVehicleForm required={activeTab === "manual"} nested={["vehicle"]} hideVehicleType={true} />
        </TabPane>
      </Tabs>
      <div className="ml-2 mr-2">
        <Form.Item name="note" label={_t("note")}>
          <TextArea placeholder={_t("note_placeholder")} autoSize={{ minRows: 1, maxRows: 3 }} />
        </Form.Item>

        <div className="uploadRef" ref={uploadRef}>
          <Form.Item name="files" label={_t("documents")} valuePropName="fileList" getValueFromEvent={normFile}>
            <DocumentsUpload
              canLockFiles={hasPermission("tax.isAdmin")}
              action={taxFileUrl(taxCase?.id)}
              canDelete={!isAdmin}
              multiple={true}
            />
          </Form.Item>
        </div>
      </div>
    </div>
  );
};

export default TaxFetchCarFC;

function VinPreview({ vehicle }: { vehicle: RemoteVehicleData | null }) {
  if (vehicle) {
    const dmrVehicle = vehicle.dmr
    const data = dmrVehicle?.mapped
    const numberPlates = dmrVehicle?.raw?.data?.registration
    return (
      <Alert type="info" className="mb-1" showIcon message={
        <div>
          <span> {data?.brand}</span>
          <span> {data?.model}</span>
          {data?.equipmentVariant && data?.equipmentVariant !== "-" && (
            <span> {data?.equipmentVariant}</span>
          )}
          {data?.horsePower && (
            <span> {data?.horsePower} HK</span>
          )}
          {numberPlates && (
            <span>, {numberPlates}</span>
          )}
          {data?.type && (
            <span>, {data?.type}</span>
          )}
        </div>
      } />
    )
  }

  return null
}



function LinkPreview({ vehicle }: { vehicle: ScrapedVehicleType | null }) {

  if (vehicle) {
    const data = vehicle.vehicle
    const type = vehicle.vehicle.type // Personbil vs Varebil
    return (
      <Alert type="info" className="mb-1" showIcon message={
        <div>
          <span> {data?.brand}</span>
          <span> {data?.model}</span>
          {data?.equipmentVariant && data?.equipmentVariant !== "-" && (
            <span> {data?.equipmentVariant}</span>
          )}
          {data?.horsePower && (
            <span> {data?.horsePower} {data?.emissionAmount === 0 ? "KW" : "HP"}</span>
          )}
          {type && (
            <span>, {type}</span>
          )}
        </div>
      } />
    )
  }

  return null
}
